import { ReactNode, createContext, useContext, useMemo, useSyncExternalStore } from 'react';
import { SheetSection } from './Sheet';

/**
 * The outline beside a report being filled in: its sections, every field the
 * document asks for — filled or not — and the sign-offs, each a jump away,
 * with what is left before the report can be published.
 *
 * Fields register while the document renders (ReportField, SignOffs, a
 * composed report's field blocks); the sheet publishes what it collected once
 * the render commits, and the rail reads that. The page provides the store;
 * a document rendered anywhere else registers nothing.
 */

export interface OutlineEntry {
  /** the element to scroll to */
  id: string;
  label: string;
  kind: 'field' | 'signoff';
  filled: boolean;
  /** leaving it empty doesn't hold the report back */
  optional: boolean;
  /** the section it prints in */
  section: string | null;
}

export interface OutlineSnapshot {
  sections: SheetSection[];
  entries: OutlineEntry[];
}

const EMPTY: OutlineSnapshot = { sections: [], entries: [] };

export class OutlineStore {
  private pending: OutlineEntry[] = [];
  private seen = new Set<string>();
  private snapshot: OutlineSnapshot = EMPTY;
  private listeners = new Set<() => void>();

  beginPass(): void {
    this.pending = [];
    this.seen.clear();
  }

  add(entry: OutlineEntry): void {
    if (this.seen.has(entry.id)) {
      // an element re-rendering on its own updates its entry in place
      this.pending = this.pending.map((e) => (e.id === entry.id ? entry : e));
      return;
    }
    this.seen.add(entry.id);
    this.pending.push(entry);
  }

  commit(sections: SheetSection[]): void {
    const next: OutlineSnapshot = { sections: [...sections], entries: [...this.pending] };
    if (JSON.stringify(next) === JSON.stringify(this.snapshot)) return;
    this.snapshot = next;
    this.listeners.forEach((l) => l());
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): OutlineSnapshot => this.snapshot;
}

const OutlineContext = createContext<OutlineStore | null>(null);

export function ReportOutlineProvider({ store, children }: { store: OutlineStore; children: ReactNode }) {
  return <OutlineContext.Provider value={store}>{children}</OutlineContext.Provider>;
}

export function useOutlineStore(): OutlineStore | null {
  return useContext(OutlineContext);
}

/** A page's outline store, stable for the life of the page. */
export function useNewOutlineStore(): OutlineStore {
  return useMemo(() => new OutlineStore(), []);
}

export function useOutline(store: OutlineStore | null): OutlineSnapshot {
  const noop = useMemo(() => new OutlineStore(), []);
  const target = store ?? noop;
  return useSyncExternalStore(target.subscribe, target.getSnapshot, target.getSnapshot);
}

/** What is still needed before Publish: required fields left empty, and unsigned required sign-offs. */
export function outstanding(snapshot: OutlineSnapshot): OutlineEntry[] {
  return snapshot.entries.filter((e) => !e.filled && !e.optional);
}

const jump = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });

/** The rail: sections to jump to, then what the owner fills in and signs, filled or not. */
export function ReportOutline({ store, title = 'In this report' }: { store: OutlineStore; title?: ReactNode }) {
  const snapshot = useOutline(store);
  const left = outstanding(snapshot);
  const fields = snapshot.entries.filter((e) => e.kind === 'field');
  const signoffs = snapshot.entries.filter((e) => e.kind === 'signoff');
  return (
    <nav className="report-outline" aria-label="Report outline">
      <p className="report-outline-title">{title}</p>
      <p className={`report-outline-status ${left.length ? 'open' : 'done'}`}>
        {left.length === 0 ? 'Nothing left to fill in or sign' : `${left.length} left to fill in or sign`}
      </p>
      {snapshot.sections.length > 0 && (
        <ol className="report-outline-sections">
          {snapshot.sections.map((s) => (
            <li key={s.key}><button type="button" onClick={() => jump(s.anchor)}>{s.number ? `${s.number} · ` : ''}{s.title}</button></li>
          ))}
        </ol>
      )}
      {fields.length > 0 && (
        <>
          <p className="report-outline-group">To fill in</p>
          <ul className="report-outline-entries">
            {fields.map((e) => (
              <li key={e.id} className={e.filled ? 'filled' : e.optional ? 'optional' : 'missing'}>
                <button type="button" onClick={() => jump(e.id)}>
                  <span className="report-outline-mark" aria-hidden>{e.filled ? '✓' : '○'}</span>
                  {e.label}{e.optional && !e.filled ? ' (optional)' : ''}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {signoffs.length > 0 && (
        <>
          <p className="report-outline-group">Sign-offs</p>
          <ul className="report-outline-entries">
            {signoffs.map((e) => (
              <li key={e.id} className={e.filled ? 'filled' : e.optional ? 'optional' : 'missing'}>
                <button type="button" onClick={() => jump(e.id)}>
                  <span className="report-outline-mark" aria-hidden>{e.filled ? '✓' : '○'}</span>
                  {e.label}{e.optional && !e.filled ? ' (optional)' : ''}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </nav>
  );
}
